/*  iceberg-ui  */
import {
    IconWithVariant,
    Modal,
    ModalFooter,
    Select
} from '@auviknetworks/iceberg-ui';

/*  MUI  */
import {
    Alert,
    Box,
    Button,
    FormControl,
    FormLabel,
    Typography
} from '@mui/material';

/* Shared */
import { useManagedTableV2Context } from 'application/Shared/Components/Table';

/*  Hooks  */
import { usePostUserMerge } from 'hooks/dataAccess/User/userMerge';

/*  Objects  */
import { getFullName } from 'objects/User';

/*  React  */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

const MergeUsersModalBody = ({ setSourceUser, setTargetUser }) => {
    const { t } = useTranslation();
    const { table } = useManagedTableV2Context();

    const users = table.getSelectedRowModel().rows.map((user) => user.original);

    const options = Object.values(users).map((opt, index) => ({
        label: getFullName(opt),
        rowId: opt.id,
        value: index
    }));
    const [targetIndex, setTargetIndex] = useState('');

    const sourceUser = users.find(
        (user) => user.id === options[targetIndex === 0 ? 1 : 0].rowId
    );

    const targetUser = users.find(
        (user) => user.id === options[targetIndex]?.rowId
    );

    useEffect(() => {
        setTargetUser(targetUser);
        setSourceUser(sourceUser);
    }, [setSourceUser, setTargetUser, sourceUser, targetUser]);

    const sourceName = targetIndex !== '' && getFullName(sourceUser);

    return (
        <Box p="24px" data-testid="adminMergeUsersModalMergeModal">
            <Box mb="24px">
                <Typography>{t('Users.mergeUsers.mergeModal.info')}</Typography>
            </Box>
            <FormControl
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '100%'
                }}
            >
                <Box display="flex" alignItems="center" mb="24px" width="100%">
                    <Box width="25%">
                        <FormLabel>{t('Users.mergeUsers.target')}</FormLabel>
                    </Box>
                    <Box
                        width="75%"
                        data-testid="adminMergeUsersModalSelectTarget"
                    >
                        <Select
                            value={targetIndex}
                            onChange={(e) => {
                                setTargetIndex(e.value);
                            }}
                            options={options}
                            placeholder=""
                        />
                    </Box>
                </Box>
                <Box display="flex" alignItems="center" width="100%">
                    <Box width="25%">
                        <FormLabel>{t('Users.mergeUsers.source')}</FormLabel>
                    </Box>
                    <Box
                        width="75%"
                        data-testid="adminMergeUsersModalSelectSource"
                    >
                        {sourceName || (
                            <Typography sx={{ color: 'text.disabled' }}>
                                {t('Users.mergeUsers.firstSelectUser')}
                            </Typography>
                        )}
                    </Box>
                </Box>
            </FormControl>
        </Box>
    );
};

const ConfirmMergeModalBody = ({ sourceUser, targetUser }) => {
    const { t } = useTranslation();

    return (
        <Box p="24px" data-testid="adminMergeUsersModalConfirmModal">
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                mb="24px"
            >
                <IconWithVariant
                    src="WarningTriangleFilled"
                    size="64px"
                    variant="danger"
                    wrapperProps={{
                        alignContent: 'center',
                        display: 'flex',
                        marginBottom: '24px'
                    }}
                />
                <Alert severity="error" icon={false}>
                    {t('Users.mergeUsers.confirmMergeModal.alert')}
                </Alert>
            </Box>
            <Box mb="24px">
                <Typography
                    component="p"
                    dangerouslySetInnerHTML={{
                        __html: t(
                            'Users.mergeUsers.confirmMergeModal.mergeUsersInfo',
                            {
                                source: getFullName(sourceUser),
                                target: getFullName(targetUser)
                            }
                        )
                    }}
                />
            </Box>
            <Box>
                <Typography variant="p">
                    {t('Users.mergeUsers.confirmMergeModal.mergeUsersConfirm')}
                </Typography>
            </Box>
        </Box>
    );
};

export const AdminMergeUsersModal = ({ onCloseMergeModal, openMergeModal }) => {
    const { t } = useTranslation();
    const { clientId } = useParams();
    const [openConfirmMergeModal, setOpenConfirmMergeModal] = useState(false);
    const [sourceUser, setSourceUser] = useState(null);
    const [targetUser, setTargetUser] = useState(null);

    const { postUserMerge } = usePostUserMerge(t);

    return (
        <Modal
            aria-labelledby="merge-users-dialog"
            data-testid="adminMergeUsersModal"
            onClose={() => onCloseMergeModal()}
            open={openMergeModal}
            title={t('Users.mergeUsers')}
            width="675px"
        >
            {!openConfirmMergeModal ? (
                <MergeUsersModalBody
                    setSourceUser={setSourceUser}
                    setTargetUser={setTargetUser}
                />
            ) : (
                <ConfirmMergeModalBody
                    sourceUser={sourceUser}
                    targetUser={targetUser}
                />
            )}
            <ModalFooter justifyContent="space-between">
                <Button
                    color="primary"
                    variant="outlined"
                    data-testid="adminMergeUsersModalCancelBtn"
                    data-trackid="admin_users_userMerge_merge_cancel"
                    onClick={() => onCloseMergeModal()}
                >
                    {t('Common.cancel')}
                </Button>
                {!openConfirmMergeModal ? (
                    <Button
                        color="primary"
                        variant="contained"
                        data-testid="adminMergeUsersModalSaveBtn"
                        data-trackid="admin_users_userMerge_merge_continue"
                        disabled={!sourceUser || !targetUser}
                        onClick={() => {
                            setOpenConfirmMergeModal(true);
                        }}
                    >
                        {t('Common.save')}
                    </Button>
                ) : (
                    <Button
                        color="primary"
                        variant="contained"
                        data-testid="adminMergeUsersModalYesBtn"
                        data-trackid="admin_users_userMerge_merge_agree"
                        onClick={async () => {
                            await postUserMerge({
                                params: {
                                    platformTenantId: clientId,
                                    sourceUserId: sourceUser.id,
                                    targetUserId: targetUser.id
                                }
                            });
                            setOpenConfirmMergeModal(false);
                            onCloseMergeModal();
                        }}
                    >
                        {t('Common.yes')}
                    </Button>
                )}
            </ModalFooter>
        </Modal>
    );
};
